import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { FaPause, FaPlay, FaVolumeHigh, FaVolumeOff } from 'react-icons/fa6';
import ReactPlayer from 'react-player';

import { imageUrlFor } from '../../utils/image-url';
import * as styles from './hosted-video.module.css';

// Helper function to push events to dataLayer (GA4 style)
const pushVideoEvent = (eventName, additionalParams = {}) => {
	if (window?.dataLayer) {
		const eventData = {
			event: eventName,
			gtm: {
				uniqueEventId: Date.now() + Math.random(),
			},
			...additionalParams,
		};

		window.dataLayer.push(eventData);
	}
};

export function HostedVideo({
	key,
	quoteOverlay,
	thumbnail,
	video,
	videoHeightStyle,
	videoPlayOptions,
}) {
	const [playing, setPlaying] = useState(true);
	const [muted, setMuted] = useState(true);
	const [hoverMutedPlay, setHoverMutedPlay] = useState(false);
	const [hasInteracted, setHasInteracted] = useState(false);
	const [thumbnailHidden, setThumbnailHidden] = useState(false);

	const playerRef = useRef(null);
	const videoCompletedRef = useRef(false);
	const completedMilestonesRef = useRef(new Set());

	// Track video progress and milestones
	const handleProgress = (state) => {
		// Don't track progress if video is completed
		if (videoCompletedRef.current) return;

		const progress = state.played * 100;
		const milestones = [10, 50, 90];

		// Track progress milestones
		milestones.forEach((milestone) => {
			if (
				progress >= milestone &&
				!completedMilestonesRef.current.has(milestone)
			) {
				// Mark milestone as completed immediately
				completedMilestonesRef.current.add(milestone);

				pushVideoEvent('video_progress', {
					progress_milestone: milestone,
				});
			}
		});
	};

	// Track video start
	const handlePlay = () => {
		pushVideoEvent('video_start');
	};

	// Track video complete
	const handleEnded = () => {
		videoCompletedRef.current = true;
		pushVideoEvent('video_complete');
	};

	// Reset tracking when video changes
	useEffect(() => {
		videoCompletedRef.current = false;
		completedMilestonesRef.current.clear();
	}, [video?.asset?.url]);

	const handleMouseEnter = () => {
		if (!hasInteracted) {
			setHasInteracted(true);
		}
		setHoverMutedPlay(true);
	};

	const handleMouseLeave = () => {
		setHoverMutedPlay(false);
	};

	const fadeThumbnail = () => {
		setThumbnailHidden(true); // Hide the thumbnail when the video is ready
	};

	switch (videoPlayOptions) {
		case 'autoplayLoop':
			// AUTOPLAY ON LOOP:
			// No thumbnail
			// autoplay with pause/mute controls on hover
			// Optional static caption at beginning

			return (
				<div
					className={styles.testimonialWrapper}
					style={videoHeightStyle}
				>
					<ReactPlayer
						className='react-player'
						// TODO: implement when we support subtitles
						// config={{
						// 	file: {
						// 		tracks: [
						// 			{
						// 				default: true, // Make this subtitle the default one
						// 				kind: 'subtitles', // Type of track
						// 				label: 'English', // Subtitle label
						// 				src: 'https://www.example.com/subtitles-en.vtt', // Path to subtitle file
						// 				srcLang: 'en', // Language of subtitles
						// 			},
						// 		],
						// 	},
						// }}
						controls={false} // Hide default controls
						height='100%'
						loop
						muted={muted}
						onEnded={handleEnded}
						onPlay={handlePlay}
						onProgress={handleProgress}
						playIcon={<></>}
						playing={playing}
						ref={playerRef}
						url={video?.asset?.url}
						width='100%'
					/>
					{quoteOverlay && (
						<div className={styles.testimonialQuote}>
							<blockquote>{quoteOverlay}</blockquote>
						</div>
					)}
					<div className={styles.controls}>
						<button
							aria-label={playing ? 'Pause' : 'Play'}
							onClick={() => setPlaying(!playing)}
						>
							{playing ? <FaPause /> : <FaPlay />}
						</button>
						<button
							aria-label={muted ? 'Unmute' : 'Mute'}
							onClick={() => setMuted(!muted)}
						>
							{muted ? <FaVolumeOff /> : <FaVolumeHigh />}
						</button>
					</div>
				</div>
			);

		case 'hoverMutedLoop':
			// HOVER TO PLAY
			// no controls
			// start in light mode
			// no captions—atmosphere only

			return (
				<div
					className={styles.testimonialWrapper}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					style={videoHeightStyle}
				>
					{thumbnail && (
						<img
							alt='Thumbnail'
							className={classNames(
								styles.hoverplayThumbnail,
								thumbnailHidden && styles.loaded
							)}
							src={imageUrlFor(thumbnail?.asset).url()}
						/>
					)}
					<ReactPlayer
						className='react-player'
						controls={false} // Hide default controls
						height='100%'
						key={key}
						light={
							!hasInteracted
								? imageUrlFor(thumbnail?.asset).url()
								: undefined
						}
						loop
						muted
						onEnded={handleEnded}
						onPlay={handlePlay}
						onProgress={handleProgress}
						onReady={fadeThumbnail}
						playIcon={<></>}
						playing={hoverMutedPlay}
						playsinline={true}
						ref={playerRef}
						url={video?.asset?.url}
						width='100%'
					/>
				</div>
			);

		default:
			return null; // Return null if no valid videoPlayOptions match
	}
}
